import { formatNOK, formatPercent } from "@/lib/format";
import {
  bfLevert,
  bfForecast,
  closedeSalg,
  bfGap,
  deriveBfTarget,
  unitGoalStatus,
} from "@/lib/dashboard-bf-calc";
import { usePeriodFilter, usePersonFilter } from "@/contexts/dashboard-filter-context";
import { useSalesDashboardData } from "@/hooks/use-sales-dashboard-data";
import { useOfferPipeline } from "@/hooks/use-offer-pipeline";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { KpiCard, type KpiTone } from "./KpiCard";

/** Percent of target, rounded; null when there is no target base. */
function pctOf(value: number, target: number): number | null {
  return target > 0 ? Math.round((value / target) * 100) : null;
}

/**
 * F1 KPI row: BF levert, BF Forecast, Closede salg, BF-gap. Container that
 * pulls raw data once and reads the period/person filters, then delegates all
 * math to dashboard-bf-calc. Reacts to both filters automatically.
 *
 * Målsemantikk (låst av Erik 16.07, VIB-1285): med person valgt viser kortene
 * personens BIDRAG som andel av enhetens FULLE mål — aldri individuell
 * måloppnåelse. Oppnådd-status (tone + BF-gap) er alltid ENHETENS
 * (`unitGoalStatus`): sum av medlemmenes attribuerte BF mot felles enhetsmål.
 */
export function SalesKpiCards() {
  const { period } = usePeriodFilter();
  const { selectedUserId } = usePersonFilter();
  const { projects, owners, salesUnits, isLoading } = useSalesDashboardData();
  const pipelineEnabled = useFeatureFlag("feature_offer_pipeline");
  const pipeline = useOfferPipeline();

  const opts = { userId: selectedUserId };
  const now = new Date();
  const personView = !!selectedUserId;

  // Person: enhetens fulle periodemål (deles aldri på medlemsantall).
  const target = deriveBfTarget({ salesUnits, period, userId: selectedUserId });
  const levert = bfLevert(projects, owners, period, opts);
  const forecast = bfForecast(projects, owners, period, opts);
  const closed = closedeSalg(projects, owners, period, opts);

  // Oppnådd-status er alltid enhetens når en person er valgt.
  const unitStatus = personView
    ? unitGoalStatus({ projects, owners, salesUnits, period, userId: selectedUserId! })
    : null;
  const gap = personView
    ? bfGap(unitStatus!.targetKr, unitStatus!.unitLevertKr, period, now)
    : bfGap(target, levert, period, now);

  const levertPct = pctOf(levert, target);
  const forecastPct = pctOf(forecast, target);

  const subtitleOf = (pct: number | null): string => {
    if (pct == null) return "Mål ikke satt";
    return personView ? `${formatPercent(pct)} av enhetsmålet` : `${formatPercent(pct)} av mål`;
  };

  // Tone = status: personens kort farges av ENHETENS oppnåelse, aldri egen.
  const achievedTone = (pct: number | null): KpiTone => {
    if (personView) return unitStatus!.achieved ? "positive" : "default";
    return pct != null && pct >= 100 ? "positive" : "default";
  };

  const gapTone: KpiTone = gap.gapKr <= 0 ? "positive" : "negative";

  // VIB-1304, bak feature_offer_pipeline: team-total eller per-selger
  // (writers-attribusjon) vektet sum av gyldige sent-tilbud. Egen KPI ved
  // siden av BF-kortene over — pipelineBf()/bfForecast() rører vi ikke.
  const pipelineWeightedKr = personView
    ? (pipeline.bySeller.get(selectedUserId!)?.weightedKr ?? 0)
    : pipeline.total.weightedKr;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <KpiCard
        title={personView ? "GEEK levert (bidrag)" : "GEEK levert"}
        value={formatNOK(levert)}
        subtitle={subtitleOf(levertPct)}
        pct={levertPct}
        tone={achievedTone(levertPct)}
        isLoading={isLoading}
      />
      <KpiCard
        title="GEEK-prognose"
        value={formatNOK(forecast)}
        subtitle={subtitleOf(forecastPct)}
        pct={forecastPct}
        tone="warning"
        isLoading={isLoading}
      />
      <KpiCard
        title="Closede salg"
        value={formatNOK(closed.valueKr)}
        subtitle={`Estimert GEEK ${formatNOK(closed.estBfKr)}`}
        isLoading={isLoading}
      />
      <KpiCard
        title={personView ? "GEEK-gap (enhet)" : "GEEK-gap"}
        value={formatNOK(gap.gapKr)}
        subtitle={
          personView && unitStatus!.targetKr === 0
            ? "Mål ikke satt"
            : gap.gapKr <= 0
              ? personView
                ? "Enhetens mål nådd"
                : "Mål nådd"
              : `${formatNOK(gap.behovPerUkeKr)}/uke nødvendig`
        }
        tone={gapTone}
        isLoading={isLoading}
      />
      {pipelineEnabled && (
        <KpiCard
          title={personView ? "Vektet tilbudspipeline (bidrag)" : "Vektet tilbudspipeline"}
          value={formatNOK(pipelineWeightedKr)}
          subtitle={`Rå sum ${formatNOK(personView ? (pipeline.bySeller.get(selectedUserId!)?.rawKr ?? 0) : pipeline.total.rawKr)}`}
          isLoading={pipeline.isLoading}
        />
      )}
    </div>
  );
}
